import React, { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import config from './config';
import { getOrganizationContext, getOrganizationHeaders } from './orgContext';
import { AdminBreadcrumbs } from './admin/components/AdminUiPrimitives';
import { AdminPageHeader } from './admin/components/AdminMasterDetail';

type ScheduleStatus = 'Scheduled' | 'InProgress' | 'Completed' | 'Cancelled' | 'Rescheduled' | 'NoShow';

interface MyScheduleItem {
  id: number;
  classroomName: string;
  teacherDisplayName: string;
  title: string;
  startDateTime: string;
  endDateTime: string;
  locationType: 'InPerson' | 'Online';
  location?: string | null;
  meetingUrl?: string | null;
  status: ScheduleStatus;
  recurrenceType?: 'None' | 'Weekly';
  recurrenceEndDate?: string | null;
}

const API_BASE_URL = `${config.apiBaseUrl}/api`;

const formatDate = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString();
};

const formatTimeRange = (start: string, end: string): string => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return `${start} - ${end}`;
  }

  return `${startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${endDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

const MySchedule: React.FC = () => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<MyScheduleItem[]>([]);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadSchedule = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = await getAccessTokenSilently();
        const response = await axios.get<MyScheduleItem[]>(`${API_BASE_URL}/schedules/my?upcomingOnly=${!showAll}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            ...getOrganizationHeaders(),
          },
        });

        if (!cancelled) {
          setItems(Array.isArray(response.data) ? response.data : []);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.response?.data?.message || err?.message || 'Unable to load schedule.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadSchedule();

    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently, isAuthenticated, showAll]);

  const orgName = useMemo(() => {
    const context = getOrganizationContext();
    return context?.organizationName || context?.organizationSlug || 'Exam Masters';
  }, []);

  if (!isAuthenticated) {
    return (
      <div className="text-center py-12">
        <h1 className="text-xl font-bold text-slate-900">My Schedule</h1>
        <p className="mt-2 text-sm text-slate-600">Please sign in to view your upcoming classes.</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <AdminBreadcrumbs items={[
        { label: 'Dashboard', to: '/analytics' },
        { label: 'My Schedule', isCurrent: true },
      ]} />
      <AdminPageHeader
        title="My Schedule"
        subtitle={showAll ? `All sessions for ${orgName}` : `Upcoming classes for ${orgName}`}
        actions={[
          { key: 'toggle', label: showAll ? 'Show Upcoming' : 'Show All', variant: 'secondary', onClick: () => setShowAll(prev => !prev) },
        ]}
      />

      <div>
            {loading ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-slate-600 min-h-[200px] flex items-center justify-center">Loading schedule...</div>
            ) : error ? (
              <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-rose-700">{error}</div>
            ) : items.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 text-slate-500 min-h-[480px] flex flex-col items-center justify-center gap-2">
                <span className="text-3xl">&#128197;</span>
                <span className="text-sm font-medium">{showAll ? 'No sessions found.' : 'No upcoming classes found.'}</span>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-slate-600">
                      <th className="py-2 text-left font-semibold">Date</th>
                      <th className="py-2 text-left font-semibold">Time</th>
                      <th className="py-2 text-left font-semibold">Classroom</th>
                      <th className="py-2 text-left font-semibold">Teacher</th>
                      <th className="py-2 text-left font-semibold">Session</th>
                      <th className="py-2 text-left font-semibold">Location</th>
                      <th className="py-2 text-left font-semibold">Status</th>
                      <th className="py-2 text-left font-semibold">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={item.id} className="border-b border-slate-100">
                        <td className="py-2 text-slate-700">{formatDate(item.startDateTime)}</td>
                        <td className="py-2 text-slate-700">{formatTimeRange(item.startDateTime, item.endDateTime)}</td>
                        <td className="py-2 text-slate-700">{item.classroomName}</td>
                        <td className="py-2 text-slate-700">{item.teacherDisplayName}</td>
                        <td className="py-2 text-slate-700">
                          <div>{item.title}</div>
                          {item.recurrenceType === 'Weekly' && item.recurrenceEndDate && (
                            <div className="text-xs text-slate-500">Weekly until {formatDate(item.recurrenceEndDate)}</div>
                          )}
                        </td>
                        <td className="py-2 text-slate-700">
                          {item.locationType === 'Online'
                            ? (item.meetingUrl ? (
                                <a href={item.meetingUrl} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline">Join link</a>
                              ) : 'Online')
                            : (item.location || 'In Person')}
                        </td>
                        <td className="py-2 text-slate-700">{item.status}</td>
                        <td className="py-2">
                          <button
                            type="button"
                            onClick={() => navigate(`/my-session/${item.id}`)}
                            className="inline-flex items-center rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-indigo-700"
                          >
                            {item.status === 'InProgress' ? 'Join' : 'View'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
  );
};

export default MySchedule;
